import styles from "./Header.module.css";

import Brasil from "../../assets/brasil.svg";
import UnitedStates from "../../assets/unitedStates.svg";
import Espanha from "../../assets/espanha.svg";

import { AiOutlineSearch } from "react-icons/ai";
import { BsCart, BsPersonPlus } from "react-icons/bs";
import { MdOutlineArrowRight } from "react-icons/md";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoCloseOutline } from "react-icons/io5";

import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormEvent, useEffect, useRef, useState } from "react";
import api from "../../utils/axios/api.ts";
import { useCart } from "../../context/CartContext.tsx";
import { userIsClient } from "../../utils/userIsLogged.ts";

interface HeaderProps {
  boxShadow?: boolean;
  toggleMobileMenu?: (mobileMenuState: boolean) => void;
  mobileMenuActiveState?: boolean;
  isSmallScreen?: boolean;
  hamburgerIcon?: boolean;
}

const HEADER_FLAGS = [
  { id: "pt-br", src: Brasil },
  { id: "en-us", src: UnitedStates },
  { id: "es-es", src: Espanha },
] as const;

type HeaderFlag = (typeof HEADER_FLAGS)[number];

function headerFlagFromI18n(lng: string | undefined): HeaderFlag {
  const base = (lng ?? "pt").toLowerCase().split("-")[0];
  if (base === "en") return HEADER_FLAGS[1];
  if (base === "es") return HEADER_FLAGS[2];
  return HEADER_FLAGS[0];
}

export default function Header({
  boxShadow = false,
  isSmallScreen,
}: HeaderProps) {
  const { nOrders, changeNOrders } = useCart();
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const [isDown, setIsDown] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<HeaderFlag>(() =>
    headerFlagFromI18n(i18n.language)
  );
  const [query, setQuery] = useState<string>("");
  const [name, setName] = useState<string | null>(null);
  const [isMobileInputVisible, setIsMobileInputVisible] =
    useState<boolean>(false);
  const [showMenuMobile, setShowMenuMobile] = useState(false)
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [userType, setUserType] = useState<
    "client" | "entrepreneur" | "admin" | null
  >(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    async function getUserData() {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      if (token && userId) {
        api.get(`/user/id/token`).then((response) => {
          setName(response.data.name);
          if (response.data.role === "Client") {
            setUserType("client");
          } else if (response.data.role === "Entrepeneur") {
            setUserType("entrepreneur");
          } else if (response.data.role === "Admin") {
            setUserType("admin");
          }
        });
      }
    }

    getUserData();
  }, []);

  useEffect(() => {
    if (!isSmallScreen) {
      setIsMobileInputVisible(false);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    if (isMobileInputVisible) inputRef.current?.focus()

  }, [isMobileInputVisible])

  useEffect(() => {
    setSelectedFlag(headerFlagFromI18n(i18n.language));
  }, [i18n.language]);

  const handleChangeLanguage = (newLanguage: string) => {
    if (newLanguage === "en-us") i18n.changeLanguage("en");
    else if (newLanguage === "es-es") i18n.changeLanguage("es");
    else i18n.changeLanguage("pt");
  };

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    if (query.trim()) {
      navigate(`/search/1?name=${query.trim()}`); // Redireciona para a URL com o valor do input
    }
  };

  const handleQuery = () => {
    if (isSmallScreen && !isMobileInputVisible) {
      setIsMobileInputVisible(true);
    } else if (!isSmallScreen && isMobileInputVisible) {
      setIsMobileInputVisible(false);
    } else if (!isSmallScreen && !isMobileInputVisible) {
      handleSubmit();
    } else if (isSmallScreen && isMobileInputVisible) {
      setIsMobileInputVisible(false);
      handleSubmit();
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    changeNOrders(0);
    navigate("/");
    window.location.reload();
  };

  const handleClickOutsideDropdown = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideDropdown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);

  return (
    <header
      className={`${styles.containerHeader} ${boxShadow ? `${styles.containerHeaderWithBoxShadow}` : ""
        }`} style={{ position: isSmallScreen ? 'relative' : undefined }}
    >
      {!isSmallScreen ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {!isMobileInputVisible && (
            <>
              <Link className={styles.logo} to="/" aria-label="Home" />
              <div className={styles.headerContent}>
                <form onSubmit={handleSubmit} className={styles.containerSearch}>
                  <input
                    type="text"
                    onClick={handleQuery}
                    className={styles.inputSearch}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("header.search")}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()} // Lida com a tecla Enter
                  />
                  <button
                    className={styles.submitButtonSearch}
                    type="button"
                    onClick={handleQuery}
                  >
                    <AiOutlineSearch
                      className={styles.iconButton}
                      size={24}
                      color={"rgba(37, 37, 37, 0.40)"}
                    />
                  </button>
                </form>
                <div className={styles.flagsAndCartAndLoginContainer}>
                  {/* <div className={styles.containerFlags}>
                    <MdOutlineArrowRight
                      size={30}
                      color={"#D8372C"}
                      onClick={() => setIsDown(!isDown)}
                      className={
                        isDown
                          ? `${styles.arrow} ${styles.down}`
                          : `${styles.arrow}`
                      }
                    />
                    <div>
                      <img
                        src={selectedFlag.src}
                        alt="Selected Flag"
                        className={`${styles.flag}`}
                        onClick={() => setIsDown(!isDown)}
                      />
                      {isDown && (
                        <div className={styles.dropdownFlags}>
                          {HEADER_FLAGS.filter(
                            (flag) => flag.id !== selectedFlag.id
                          )
                            .map((flag) => (
                              <img
                                key={flag.id}
                                className={`${styles.flag}`}
                                src={flag.src}
                                alt="selected Flag"
                                onClick={() => {
                                  setSelectedFlag(flag);
                                  handleChangeLanguage(flag.id);
                                  setIsDown(false);
                                }}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  </div> */}

                  {userIsClient() && <div className={styles.cartContainer}>
                    <Link to="/carrinho">
                      <BsCart size={30} color={"#D8372C"} />
                      <span
                        style={{
                          position: "absolute",
                          right: 0,
                          bottom: -8,
                          backgroundColor: "#d8372c",
                          borderRadius: 9999,
                          width: 24,
                          height: 24,
                          fontSize: 12,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "white",
                        }}
                      >
                        {nOrders}
                      </span>
                    </Link>
                  </div>}

                  {!name ? (
                    <div className={styles.containerPerson}>
                      <div className={styles.person}>
                        <BsPersonPlus color={"#D8372C"} size={28} />
                      </div>
                      <p className={styles.textPerson}>
                        <Link className={styles.red} to={"/login"}>
                          {t("header.login")}
                        </Link>
                        &nbsp;{t("header.or")}
                        <br />
                        <Link className={styles.red} to={"/register/options"}>
                          {t("header.signin")}
                        </Link>
                      </p>
                    </div>
                  ) : (
                    <div
                      ref={dropdownRef}
                      className={styles.containerPerson}
                      onClick={() => setIsDropdownVisible(!isDropdownVisible)}
                    >
                      <span className={styles.textPerson}>Olá, {name}</span>
                      {isDropdownVisible && (
                        <div className={styles.dropdown}>
                          <span className={styles.dropdownTriangle}></span>
                          <h3>Minha Conta</h3>

                          <ul>
                            {userType === "client" ? (
                              <>
                                <li>
                                  <Link to="/auth/client/account/profile">
                                    Perfil
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/client/account/favorites">
                                    Favoritos
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/client/account/orders">
                                    Pedidos
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/client/account/chat">Chat</Link>
                                </li>
                                <li>
                                  <Link to="/auth/client/account/addresses/list">
                                    Endereços
                                  </Link>
                                </li>
                                {/* <li>
                                  <Link to="/auth/client/account/cards/list">
                                    Cartões
                                  </Link>
                                </li> */}
                              </>
                            ) : userType === "entrepreneur" ? (
                              <>
                                <li>
                                  <Link to="/auth/entrepreneur/account/data">
                                    {t("accountMenu.data")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/entrepreneur/account/orders">
                                    {t("accountMenu.orders")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/entrepreneur/account/chat">
                                    {t("accountMenu.chat")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/entrepreneur/account/products/list">
                                    {t("accountMenu.products")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/entrepreneur/account/logs">
                                    {t("accountMenu.logs")}
                                  </Link>
                                </li>
                              </>
                            ) : userType === "admin" ? (
                              <>
                                <li>
                                  <Link to="/AdminTaxas">
                                    {t("accountMenu.fees")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/AdminDashboard">
                                    {t("accountMenu.dashboard")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/auth/admin/account/chat">
                                    {t("accountMenu.chat")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/ListaUsuarios">
                                    {t("accountMenu.users")}
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/AdminRelatorio">
                                    {t("accountMenu.logs")}
                                  </Link>
                                </li>
                              </>
                            ) : (
                              <li>Tipo de usuário não reconhecido</li>
                            )}
                            <li>
                              <button
                                className={styles.logout}
                                onClick={handleLogout}
                              >
                                {t("headerDropdown.sair")}
                              </button>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {isMobileInputVisible && (
            <div className={styles.fullInputMobileContainer}>
              <div className={styles.containerSearch}>
                <input
                  className={styles.inputSearch}
                  type="text"
                  value={query}
                  ref={inputRef}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("header.search")}
                />
                <button type="button" onClick={handleQuery}>
                  <AiOutlineSearch
                    className={styles.iconButton}
                    size={24}
                    color={"rgba(37, 37, 37, 0.40)"}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setIsMobileInputVisible(false)}
                >
                  <IoCloseOutline
                    color="rgba(37, 37, 37, 0.40)"
                    className={styles.iconButton}
                    size={30}
                  />
                </button>
              </div>
            </div>
          )}
        </div>)
        : (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%', padding: '0 0 0px 0', marginBottom: 8 }}>
            <div className={styles.hamburgerIconContainer} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', margin: '0 0 8px 0', padding: 0 }}>
              {/* <GiHamburgerMenu
                size={36}
                color="#d8372c"
                onClick={
                  () => setIsDropdownVisible(!isDropdownVisible)
                }
              /> */}
              {!name ? (
                <div className={styles.containerPerson}>
                  <div className={styles.person}>
                    <BsPersonPlus color={"#D8372C"} size={28} />
                  </div>
                  <p className={styles.textPerson}>
                    <Link className={styles.red} to={"/login"}>
                      {t("header.login")}
                    </Link>
                    &nbsp;{t("header.or")}
                    <br />
                    <Link className={styles.red} to={"/register/options"}>
                      {t("header.signin")}
                    </Link>
                  </p>
                </div>
              ) : (
                <div
                  ref={dropdownRef}
                  className={styles.containerPerson}
                  style={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                >
                  <span className={styles.textPerson} style={{}} onClick={() => setIsDropdownVisible(!isDropdownVisible)}>Olá, {name}</span>
                  {isDropdownVisible && (
                    <div className={styles.dropdown} style={{ transform: 'none', left: 0, top: '100%', marginTop: 8 }}>
                      <span className={styles.dropdownTriangle}></span>
                      <h3>Minha Conta</h3>

                      <ul>
                        {userType === "client" ? (
                          <>
                            <li>
                              <Link to="/auth/client/account/profile">
                                Perfil
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/client/account/favorites">
                                Favoritos
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/client/account/orders">
                                Pedidos
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/client/account/chat">Chat</Link>
                            </li>
                            <li>
                              <Link to="/auth/client/account/addresses/list">
                                Endereços
                              </Link>
                            </li>
                            {/* <li>
                              <Link to="/auth/client/account/cards/list">
                                Cartões
                              </Link>
                            </li> */}
                          </>
                        ) : userType === "entrepreneur" ? (
                          <>
                            <li>
                              <Link to="/auth/entrepreneur/account/data">
                                {t("accountMenu.data")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/entrepreneur/account/orders">
                                {t("accountMenu.orders")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/entrepreneur/account/chat">
                                {t("accountMenu.chat")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/entrepreneur/account/products/list">
                                {t("accountMenu.products")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/entrepreneur/account/logs">
                                {t("accountMenu.logs")}
                              </Link>
                            </li>
                          </>
                        ) : userType === "admin" ? (
                          <>
                            <li>
                              <Link to="/AdminTaxas">
                                {t("accountMenu.fees")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/AdminDashboard">
                                {t("accountMenu.dashboard")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/auth/admin/account/chat">
                                {t("accountMenu.chat")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/ListaUsuarios">
                                {t("accountMenu.users")}
                              </Link>
                            </li>
                            <li>
                              <Link to="/AdminRelatorio">
                                {t("accountMenu.logs")}
                              </Link>
                            </li>
                          </>
                        ) : (
                          <li>Tipo de usuário não reconhecido</li>
                        )}
                        <li>
                          <button
                            className={styles.logout}
                            onClick={handleLogout}
                          >
                            {t("headerDropdown.sair")}
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <Link className={styles.logo} to="/" aria-label="Home" />
              <div className={styles.containerFlags} style={{ border: 0, justifyContent: 'center' }}>
                {userIsClient() && <div className={styles.cartContainer} style={{ border: 0 }}>
                  <Link to="/carrinho">
                    <BsCart size={30} color={"#D8372C"} />
                    <span
                      style={{
                        position: "absolute",
                        right: 0,
                        bottom: -8,
                        backgroundColor: "#d8372c",
                        borderRadius: 9999,
                        width: 24,
                        height: 24,
                        fontSize: 12,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "white",
                      }}
                    >
                      {nOrders}
                    </span>
                  </Link>
                </div>}
                {/* <MdOutlineArrowRight
                  size={30}
                  color={"#D8372C"}
                  onClick={() => setIsDown(!isDown)}
                  className={
                    isDown
                      ? `${styles.arrow} ${styles.down}`
                      : `${styles.arrow}`
                  }
                />
                <div style={{ display: 'flex', position: 'relative' }}>
                  <img
                    src={selectedFlag.src}
                    alt="Selected Flag"
                    className={`${styles.flag}`}
                    onClick={() => setIsDown(!isDown)}
                  />
                  {isDown && (
                    <div className={styles.dropdownFlags} style={{ top: '100%', marginTop: 4 }}>
                      {HEADER_FLAGS.filter(
                        (flag) => flag.id !== selectedFlag.id
                      ).map((flag) => (
                          <img
                            key={flag.id}
                            className={`${styles.flag}`}
                            src={flag.src}
                            alt="selected Flag"
                            onClick={() => {
                              setSelectedFlag(flag);
                              handleChangeLanguage(flag.id);
                              setIsDown(false);
                            }}
                          />
                        ))}
                    </div>
                  )}
                </div> */}
              </div>
            </div>
            <form onSubmit={handleSubmit} className={styles.containerSearch} style={{ margin: 0, border: '1px solid #d8372c', padding: 0 }}>
              <input
                type="text"
                onClick={handleQuery}
                className={styles.inputSearch}
                style={{ width: '100%' }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("header.search")}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()} // Lida com a tecla Enter
              />
            </form>
          </div>
        )}
    </header>
  );
}
